import React from 'react';
import Image from 'next/image';
import cx from 'classnames';
import RecommendedBook from '../RecommendedBook';
import UpdateNote from '../UpdateNote';
import { getAllBooks } from '../../lib/books';
import styles from '../Content/Content.module.scss';
import type { MarkdownArticleRelation } from '../../lib/articles';
import type { Book } from '../../types/common/Book.types';

const parseInline = (text: string): React.ReactNode[] => {
  const tokens: React.ReactNode[] = [];
  const pattern = /(!?\[([^\]]*)\]\(([^)]+)\)|`([^`]+)`|\*\*([^*]+)\*\*|_([^_]+)_)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text))) {
    if (match.index > lastIndex) {
      tokens.push(text.slice(lastIndex, match.index));
    }

    if (match[1].startsWith('!')) {
      tokens.push(
        <Image
          key={`image-${match.index}`}
          src={match[3]}
          width={920}
          height={520}
          alt={match[2]}
          className={styles.markdownImage}
        />
      );
    } else if (match[2] !== undefined && match[3] !== undefined) {
      tokens.push(
        <a key={`link-${match.index}`} href={match[3]} target="_blank" rel="noreferrer nofollow">
          {match[2]}
        </a>
      );
    } else if (match[4] !== undefined) {
      tokens.push(<code key={`code-${match.index}`}>{match[4]}</code>);
    } else if (match[5] !== undefined) {
      tokens.push(<strong key={`strong-${match.index}`}>{match[5]}</strong>);
    } else if (match[6] !== undefined) {
      tokens.push(<em key={`em-${match.index}`}>{match[6]}</em>);
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    tokens.push(text.slice(lastIndex));
  }

  return tokens;
};

const renderParagraph = (lines: string[], key: string) => (
  <p key={key}>
    {lines.flatMap((line, index) => {
      const inline = parseInline(line);
      return index === 0 ? inline : [<br key={`${key}-br-${index}`} />, ...inline];
    })}
  </p>
);

const renderList = (lines: string[], key: string) => {
  const isOrdered = /^\d+\.\s+/.test(lines[0]);
  const items = lines.map((line) => line.replace(/^(-|\d+\.)\s+/, ''));
  const ListTag = isOrdered ? 'ol' : 'ul';

  return (
    <ListTag key={key}>
      {items.map((item, index) => (
        <li key={`${key}-${index}`}>{parseInline(item)}</li>
      ))}
    </ListTag>
  );
};

const renderBlockquote = (lines: string[], key: string) => (
  <div key={key} className={styles.wideAsset}>
    <blockquote className={styles.blockquote}>
      {renderParagraph(lines.map((line) => line.replace(/^>\s?/, '')), `${key}-quote`)}
    </blockquote>
  </div>
);

const stripBlockquoteMarker = (line: string) => line.replace(/^>\s?/, '');

const parseUpdateNoteQuote = (lines: string[]) => {
  const quoteLines = lines.map(stripBlockquoteMarker);
  const title = quoteLines[0]?.trim();
  const updateNote = title?.match(/^\*\*(.+)\*\*\s+\((\d{4}-\d{2}-\d{2})\)$/);

  if (!updateNote) {
    return null;
  }

  return {
    date: updateNote[2],
    lines: quoteLines.slice(1).filter((line, index, contentLines) => (
      line.trim() || contentLines.slice(index + 1).some((nextLine) => nextLine.trim())
    )),
  };
};

const normalizeBookText = (value = '') => value
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[_*`]/g, '')
  .replace(/[^\w\s-]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase();

const getRelationSlug = (relation: MarkdownArticleRelation | string) => typeof relation === 'string'
  ? relation
  : relation.slug;

const getRelationType = (relation: MarkdownArticleRelation | string) => typeof relation === 'string'
  ? 'article'
  : relation.type;

const parseRecommendedBookQuote = (lines: string[]) => {
  if (lines.length !== 1) {
    return null;
  }

  const quoteText = lines[0].replace(/^>\s?/, '').trim();
  const recommendation = quoteText.match(/^(Recommended book|Polecana książka):\s+(.+)$/i);

  if (!recommendation) {
    return null;
  }

  const bookText = recommendation[2].replace(/[_*`]/g, '').trim();
  const lastCommaIndex = bookText.lastIndexOf(',');
  const title = lastCommaIndex === -1 ? bookText : bookText.slice(0, lastCommaIndex).trim();
  const author = lastCommaIndex === -1 ? '' : bookText.slice(lastCommaIndex + 1).trim();

  return {
    title,
    author,
  };
};

const findRecommendedBook = ({
  quote,
  books,
  related,
}: {
  quote: ReturnType<typeof parseRecommendedBookQuote>;
  books: Book[];
  related: MarkdownArticleRelation[];
}) => {
  if (!quote) {
    return null;
  }

  const normalizedTitle = normalizeBookText(quote.title);
  const normalizedAuthor = normalizeBookText(quote.author);
  const matchedByText = books.find((book) => {
    const bookTitle = normalizeBookText(book.title);
    const bookAuthor = normalizeBookText(book.author);
    const titleMatches = bookTitle === normalizedTitle
      || bookTitle.startsWith(normalizedTitle)
      || normalizedTitle.startsWith(bookTitle);
    const authorMatches = !normalizedAuthor || bookAuthor === normalizedAuthor;

    return titleMatches && authorMatches;
  });

  if (matchedByText) {
    return matchedByText;
  }

  const relatedBookSlugs = related
    .filter((relation) => getRelationType(relation) === 'book')
    .map((relation) => normalizeBookText(getRelationSlug(relation)));

  for (const slug of relatedBookSlugs) {
    const matchedBook = books.find((book) => {
      const bookSlug = normalizeBookText(book.slug);

      return bookSlug === slug || bookSlug.startsWith(`${slug}-`);
    });

    if (matchedBook) {
      return matchedBook;
    }
  }

  return null;
};

const renderRecommendedBookOrBlockquote = ({
  lines,
  key,
  books,
  related,
  locale,
  getHighlightedCodeBlock,
}: {
  lines: string[];
  key: string;
  books: Book[];
  related: MarkdownArticleRelation[];
  locale: string;
  getHighlightedCodeBlock: () => string | undefined;
}) => {
  const updateNote = parseUpdateNoteQuote(lines);

  if (updateNote) {
    return (
      <UpdateNote key={key} date={new Date(updateNote.date)}>
        {renderMarkdownBlocks({
          lines: updateNote.lines,
          keyPrefix: `${key}-update`,
          getHighlightedCodeBlock,
        })}
      </UpdateNote>
    );
  }

  const book = findRecommendedBook({
    quote: parseRecommendedBookQuote(lines),
    books,
    related,
  });

  if (!book) {
    return renderBlockquote(lines, key);
  }

  return <RecommendedBook key={key} book={book} locale={locale} />;
};

const formatCodeLanguage = (language = '') => language.trim().toLowerCase();

const renderCodeBlock = (code: string[], language: string, key: string, highlightedHtml?: string) => {
  const formattedLanguage = formatCodeLanguage(language);

  if (highlightedHtml) {
    return (
      <div key={key} className={styles.codeBlock}>
        {formattedLanguage && <span className={styles.codeLanguage}>{formattedLanguage}</span>}
        <div dangerouslySetInnerHTML={{ __html: highlightedHtml }} />
      </div>
    );
  }

  return (
    <pre key={key} className={styles.codeBlock}>
      {formattedLanguage && <span className={styles.codeLanguage}>{formattedLanguage}</span>}
      <code data-language={language || undefined}>{code.join('\n')}</code>
    </pre>
  );
};

const renderMarkdownBlocks = ({
  lines,
  keyPrefix,
  getHighlightedCodeBlock,
}: {
  lines: string[];
  keyPrefix: string;
  getHighlightedCodeBlock: () => string | undefined;
}) => {
  const blocks: React.ReactNode[] = [];
  let buffer: string[] = [];
  let listBuffer: string[] = [];
  let codeBuffer: string[] = [];
  let codeLanguage = '';
  let isCodeBlock = false;

  const flushParagraph = () => {
    if (buffer.length) {
      blocks.push(renderParagraph(buffer, `${keyPrefix}-paragraph-${blocks.length}`));
      buffer = [];
    }
  };

  const flushList = () => {
    if (listBuffer.length) {
      blocks.push(renderList(listBuffer, `${keyPrefix}-list-${blocks.length}`));
      listBuffer = [];
    }
  };

  lines.forEach((line) => {
    const codeFence = line.match(/^```(.*)$/);

    if (codeFence) {
      if (isCodeBlock) {
        blocks.push(renderCodeBlock(
          codeBuffer,
          codeLanguage,
          `${keyPrefix}-code-${blocks.length}`,
          getHighlightedCodeBlock()
        ));
        codeBuffer = [];
        codeLanguage = '';
        isCodeBlock = false;
      } else {
        flushParagraph();
        flushList();
        isCodeBlock = true;
        codeLanguage = codeFence[1].trim();
      }

      return;
    }

    if (isCodeBlock) {
      codeBuffer.push(line);
      return;
    }

    if (!line.trim()) {
      flushParagraph();
      flushList();
      return;
    }

    if (/^(-|\d+\.)\s+/.test(line)) {
      flushParagraph();
      listBuffer.push(line);
      return;
    }

    flushList();
    buffer.push(line);
  });

  if (isCodeBlock) {
    blocks.push(renderCodeBlock(
      codeBuffer,
      codeLanguage,
      `${keyPrefix}-code-${blocks.length}`,
      getHighlightedCodeBlock()
    ));
  }

  flushParagraph();
  flushList();

  return <>{blocks}</>;
};

const MarkdownContent = ({
  content,
  highlightedCodeBlocks = [],
  locale = 'pl',
  related = [],
}: {
  content: string;
  highlightedCodeBlocks?: string[];
  locale?: string;
  related?: MarkdownArticleRelation[];
}) => {
  const books = getAllBooks(locale);
  const lines = content.split('\n');
  const blocks: React.ReactNode[] = [];
  let buffer: string[] = [];
  let listBuffer: string[] = [];
  let quoteBuffer: string[] = [];
  let codeBuffer: string[] = [];
  let codeLanguage = '';
  let codeBlockIndex = 0;
  let isCodeBlock = false;

  const getHighlightedCodeBlock = () => {
    const highlightedCodeBlock = highlightedCodeBlocks[codeBlockIndex];
    codeBlockIndex += 1;

    return highlightedCodeBlock;
  };

  const flushParagraph = () => {
    if (buffer.length) {
      blocks.push(renderParagraph(buffer, `paragraph-${blocks.length}`));
      buffer = [];
    }
  };

  const flushList = () => {
    if (listBuffer.length) {
      blocks.push(renderList(listBuffer, `list-${blocks.length}`));
      listBuffer = [];
    }
  };

  const flushQuote = () => {
    if (quoteBuffer.length) {
      blocks.push(renderRecommendedBookOrBlockquote({
        lines: quoteBuffer,
        key: `quote-${blocks.length}`,
        books,
        related,
        locale,
        getHighlightedCodeBlock,
      }));
      quoteBuffer = [];
    }
  };

  const flushTextBlocks = () => {
    flushParagraph();
    flushList();
    flushQuote();
  };

  lines.forEach((line) => {
    const codeFence = line.match(/^```(.*)$/);

    if (codeFence) {
      if (isCodeBlock) {
        blocks.push(renderCodeBlock(
          codeBuffer,
          codeLanguage,
          `code-${blocks.length}`,
          getHighlightedCodeBlock()
        ));
        codeBuffer = [];
        codeLanguage = '';
        isCodeBlock = false;
      } else {
        flushTextBlocks();
        isCodeBlock = true;
        codeLanguage = codeFence[1].trim();
      }

      return;
    }

    if (isCodeBlock) {
      codeBuffer.push(line);
      return;
    }

    if (!line.trim()) {
      flushTextBlocks();
      return;
    }

    const heading = line.match(/^(#{2,6})\s+(.+)$/);

    if (heading) {
      flushTextBlocks();
      const HeadingTag = `h${Math.min(heading[1].length, 5)}` as keyof JSX.IntrinsicElements;
      blocks.push(<HeadingTag key={`heading-${blocks.length}`}>{parseInline(heading[2])}</HeadingTag>);
      return;
    }

    if (/^(-|\d+\.)\s+/.test(line)) {
      flushParagraph();
      flushQuote();
      listBuffer.push(line);
      return;
    }

    if (/^>\s?/.test(line)) {
      flushParagraph();
      flushList();
      quoteBuffer.push(line);
      return;
    }

    if (/^!\[/.test(line)) {
      flushTextBlocks();
      blocks.push(<figure key={`figure-${blocks.length}`} className={cx(styles.figure, styles.wideImage)}>{parseInline(line)}</figure>);
      return;
    }

    flushList();
    flushQuote();
    buffer.push(line);
  });

  if (isCodeBlock) {
    blocks.push(renderCodeBlock(
      codeBuffer,
      codeLanguage,
      `code-${blocks.length}`,
      getHighlightedCodeBlock()
    ));
  }

  flushTextBlocks();

  return <>{blocks}</>;
};

export default MarkdownContent;
